import React, { useEffect, useRef, useMemo } from "react";

function escapeHtml(str) {
    if (!str) return "";
    return str
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

function escapeRegex(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function textToHtmlWithTopics(text, matchedTopics = []) {
    if (!text) return "";

    // 1. Strip existing [[topic|context]] tags to get clean plain text
    const cleanText = text.replace(/\[\[(.*?)(?:\|(.*?))?\]\]/g, "$1");

    if (!matchedTopics || matchedTopics.length === 0) {
        const lines = cleanText.split("\n");
        return lines.map((line, idx) => {
            if (!line) return "<div><br></div>";
            const escaped = escapeHtml(line);
            return idx === 0 ? escaped : `<div>${escaped}</div>`;
        }).join("");
    }

    // 2. Sort matched topics by name length descending
    const sortedTopics = [...matchedTopics].sort(
        (a, b) => (b.name || "").length - (a.name || "").length
    );

    const names = sortedTopics.map(t => (t.name || "").trim()).filter(Boolean);
    if (names.length === 0) {
        const lines = cleanText.split("\n");
        return lines.map((line, idx) => {
            if (!line) return "<div><br></div>";
            const escaped = escapeHtml(line);
            return idx === 0 ? escaped : `<div>${escaped}</div>`;
        }).join("");
    }

    const pattern = new RegExp(`(?<=^|[^\\p{L}\\p{N}])(${names.map(escapeRegex).join("|")})(?=[^\\p{L}\\p{N}]|$)`, "gui");

    const lines = cleanText.split("\n");
    return lines.map((line, lineIdx) => {
        if (!line) return "<div><br></div>";

        const usedInLine = new Set();
        const replacedLine = line.replace(pattern, (match, matchedWord) => {
            const word = matchedWord || match;
            const foundTopic = sortedTopics.find(
                t => t.name.toLowerCase() === word.toLowerCase() && !usedInLine.has((t.context || "").toLowerCase())
            );

            if (!foundTopic) {
                return match;
            }

            usedInLine.add((foundTopic.context || "").toLowerCase());

            const ctxBadge = foundTopic.context
                ? `<span class="text-[10px] px-1 py-0.2 rounded bg-orange-200/70 text-orange-800 font-normal select-none pointer-events-none">${escapeHtml(foundTopic.context)}</span>`
                : "";

            return `<span class="inline-flex items-center gap-1 mx-1 px-1.5 py-0.5 rounded bg-orange-50 text-orange-900 border border-orange-200/90 font-semibold text-xs leading-none shadow-xs select-none align-middle" contenteditable="false" data-topic="${escapeHtml(foundTopic.name)}" data-context="${escapeHtml(foundTopic.context || "")}"><span class="font-bold select-none pointer-events-none">${escapeHtml(foundTopic.name)}</span>${ctxBadge}</span>`;
        });

        return lineIdx === 0 ? replacedLine : `<div>${replacedLine}</div>`;
    }).join("");
}

function domToText(container) {
    if (!container) return "";
    let text = "";

    function walk(node) {
        if (node.nodeType === Node.TEXT_NODE) {
            text += node.textContent.replace(/\u00A0/g, " ");
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.hasAttribute("data-topic")) {
                const topic = node.getAttribute("data-topic");
                const context = node.getAttribute("data-context");
                text += context ? `[[${topic}|${context}]]` : `[[${topic}]]`;
            } else if (node.tagName === "BR") {
                text += "\n";
            } else {
                const isBlock = ["DIV", "P"].includes(node.tagName);
                if (isBlock && text.length > 0 && !text.endsWith("\n")) {
                    text += "\n";
                }
                for (const child of node.childNodes) {
                    walk(child);
                }
            }
        }
    }

    for (const child of container.childNodes) {
        walk(child);
    }
    return text;
}

function saveCaretPosition(context) {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0);
    const preRange = range.cloneRange();
    preRange.selectNodeContents(context);
    preRange.setEnd(range.endContainer, range.endOffset);
    return preRange.toString().length;
}

function restoreCaretPosition(context, targetCharIndex) {
    if (targetCharIndex == null) return;
    const selection = window.getSelection();
    if (!selection) return;

    let charCount = 0;
    const range = document.createRange();
    range.setStart(context, 0);
    range.collapse(true);

    let found = false;

    function walk(node) {
        if (found) return;

        if (node.nodeType === Node.TEXT_NODE) {
            const nextCharCount = charCount + node.textContent.length;
            if (targetCharIndex <= nextCharCount) {
                range.setStart(node, Math.max(0, targetCharIndex - charCount));
                range.collapse(true);
                found = true;
                return;
            }
            charCount = nextCharCount;
        } else if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.hasAttribute("data-topic")) {
                const badgeTextLen = (node.textContent || "").length;
                if (targetCharIndex <= charCount + badgeTextLen) {
                    range.setStartAfter(node);
                    range.collapse(true);
                    found = true;
                    return;
                }
                charCount += badgeTextLen;
            } else if (node.tagName === "BR") {
                charCount += 1;
            } else {
                for (const child of node.childNodes) {
                    walk(child);
                }
            }
        }
    }

    walk(context);

    if (!found) {
        range.selectNodeContents(context);
        range.collapse(false);
    }

    selection.removeAllRanges();
    selection.addRange(range);
}

/**
 * KeywordSignalView:
 * Renders an interactive keyword input that highlights matched topics as styled chip badges.
 * Stops Enter key propagation so it never triggers background shortcuts/modals.
 */
export default function KeywordSignalView({
    value = "",
    onChange,
    matchedTopics = [],
    disabled = false,
    className = "",
}) {
    const editorRef = useRef(null);
    const isComposingRef = useRef(false);
    const lastMatchedSignatureRef = useRef("");

    // Create a stable signature of matched topics
    const matchedSignature = useMemo(() => {
        return (matchedTopics || [])
            .map(t => `${t.id || t.name}:${t.context}`)
            .sort()
            .join("|");
    }, [matchedTopics]);

    // Update DOM HTML when value or matchedTopics changes
    useEffect(() => {
        if (!editorRef.current) return;

        const currentDomText = domToText(editorRef.current);
        const hasTextChanged = currentDomText.trim() !== (value || "").trim();
        const hasTopicsChanged = lastMatchedSignatureRef.current !== matchedSignature;

        if (hasTextChanged || hasTopicsChanged) {
            lastMatchedSignatureRef.current = matchedSignature;

            // Preserve caret if focused
            const isFocused = document.activeElement === editorRef.current;
            const caretPos = isFocused ? saveCaretPosition(editorRef.current) : null;

            editorRef.current.innerHTML = textToHtmlWithTopics(value, matchedTopics);

            if (isFocused && caretPos !== null) {
                restoreCaretPosition(editorRef.current, caretPos);
            }
        }
    }, [value, matchedSignature]);

    const handleInput = () => {
        if (!editorRef.current || isComposingRef.current) return;

        const newText = domToText(editorRef.current);
        if (onChange) {
            onChange(newText);
        }
    };

    const handleKeyDown = (e) => {
        e.stopPropagation();
    };

    const handleKeyUp = (e) => {
        e.stopPropagation();
    };

    return (
        <div
            ref={editorRef}
            contentEditable={!disabled}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onCompositionStart={() => { isComposingRef.current = true; }}
            onCompositionEnd={() => { isComposingRef.current = false; handleInput(); }}
            className={`w-full min-h-[58px] max-h-[140px] overflow-y-auto px-3 py-2 text-xs font-mono border border-zinc-200 rounded-lg outline-none bg-white text-zinc-800 focus:border-[#FF8C39] focus:ring-1 focus:ring-[#FF8C39] transition-all leading-relaxed whitespace-pre-wrap break-words cursor-text select-text ${className}`}
            style={{ minHeight: "56px" }}
        />
    );
}
